import React, { useEffect, useCallback, useState } from "react";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/common/PageHeader";
import AppContext from "../context/AppContext";
import MetaData from "../helpers/MetaData";
import { HiOutlineDownload } from "react-icons/hi";
import notification from "../helpers/notification";
import ApiService from "../api/ApiService";
import moment from "moment";
import fileDownload from "js-file-download";
import GoToTop from "../helpers/GoToTop";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Tooltip from "@mui/material/Tooltip";
import DonationTable from "../components/common/DonationTable";
import PurchaseTable from "../components/common/PurchaseTable";
import NityasevaTable from "../components/common/NityasevaTable";
import DownloadTenbe from "../components/10BE/downloadTenbe";

const DmsAllTransactions = () => {
  const navigate = useNavigate();
  const { donor } = useContext(AppContext);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('donations');
  
  // State management
  const [donations, setDonations] = useState([]);
  const [purchases, setSales] = useState([]);
  const [nityasevas, setNityaseva] = useState([]);
  const [paginationDisable, setPaginationDisable] = useState(false);
  
  // Pagination states for each tab
  const [donationPage, setDonationPage] = useState(1);
  const [donationTotalPages, setDonationTotalPages] = useState(0);
  const [salepage, setSalepage] = useState(1);
  const [totalsalePages, setTotalsalepages] = useState(0);
  const [nityapage, setNityapage] = useState(1);
  const [totalnityaPages, setTotalnityapages] = useState(0);

  // Tab configuration
  const tabs = [
    // { id: 'all', label: 'All Transactions', icon: '' },
    { id: 'donations', label: 'All Donations', icon: '' },
    { id: 'nityaseva', label: 'Nitya Seva', icon: '' },
    { id: 'purchases', label: 'Purchases', icon: '' }
  ];

  // API calls with error handling
  const fetchAllDonations = useCallback(async (id, currentPage = donationPage) => {
    try {
      setPaginationDisable(true);
      const response = await ApiService.fetchAllDonations(id, currentPage);
      
      if (response) {
        setDonations(response.donations || []);
        setDonationPage(+response.page);
        setDonationTotalPages(+response.totalPages);
      }
    } catch (err) {
      console.error('Error fetching donations:', err);
      notification('error', 'Failed to fetch donations');
      setDonations([]);
    } finally {
      setPaginationDisable(false);
    }
  }, []);

  const fetchSale = useCallback(async (id, currentPage = salepage) => {
    try {
      setPaginationDisable(true);
      const response = await ApiService.fetchSale(id, currentPage);
      
      if (response) {
        setSales(response.donations || []);
        setSalepage(+response.page);
        setTotalsalepages(+response.totalPages);
      }
    } catch (err) {
      console.error('Error fetching sales:', err);
      notification('error', 'Failed to fetch purchase history');
      setSales([]);
    } finally {
      setPaginationDisable(false);
    }
  }, []);

  const fetchNityaseva = useCallback(async (id, currentPage = nityapage) => {
    try {
      setPaginationDisable(true);
      const response = await ApiService.fetchNityaseva(id, currentPage);
      
      if (response) {
        setNityaseva(response.donations || []);
        setNityapage(+response.page);
        setTotalnityapages(+response.totalPages);
      }
    } catch (err) {
      console.error('Error fetching nitya seva:', err);
      notification('error', 'Failed to fetch Nitya Seva records');
      setNityaseva([]);
    } finally {
      setPaginationDisable(false);
    }
  }, []);

  // Handle status updates - refresh Nitya Seva data
  const handleNityasevaStatusUpdate = useCallback(() => {
    if (donor) {
      fetchNityaseva(donor.id, nityapage);
    }
  }, [donor, fetchNityaseva, nityapage]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Fetch data for the selected tab if not already loaded
    if (donor?.id) {
      switch(tabId) {
        case 'donations':
          if (donations.length === 0) {
            fetchAllDonations(donor.id, 1);
          }
          break;
        case 'nityaseva':
          if (nityasevas.length === 0) {
            fetchNityaseva(donor.id, 1);
          }
          break;
        case 'purchases':
          if (purchases.length === 0) {
            fetchSale(donor.id, 1);
          }
          break;
        case 'all':
        default:
          // Load all data for 'all' tab
          if (donations.length === 0) fetchAllDonations(donor.id, 1);
          if (nityasevas.length === 0) fetchNityaseva(donor.id, 1);
          if (purchases.length === 0) fetchSale(donor.id, 1);
          break;
      }
    }
  };

  // Initial data fetch based on active tab
  useEffect(() => {
    if (donor?.id) {
      handleTabChange(activeTab);
    }
  }, [donor?.id]);

  // Pagination effects for individual tabs
  useEffect(() => {
    if (donor?.id && (activeTab === 'donations' || activeTab === 'all')) {
      fetchAllDonations(donor.id, donationPage);
    }
  }, [donationPage, donor?.id, fetchAllDonations, activeTab]);

  useEffect(() => {
    if (donor?.id && (activeTab === 'purchases' || activeTab === 'all')) {
      fetchSale(donor.id, salepage);
    }
  }, [salepage, donor?.id, fetchSale, activeTab]);

  useEffect(() => {
    if (donor?.id && (activeTab === 'nityaseva' || activeTab === 'all')) {
      fetchNityaseva(donor.id, nityapage);
    }
  }, [nityapage, donor?.id, fetchNityaseva, activeTab]);

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch(activeTab) {
      case 'donations':
        return (
          <DonationTable
            donations={donations}
            title="Donations"
            totalPages={donationTotalPages}
            page={donationPage}
            setPage={setDonationPage}
            paginationDisable={paginationDisable}
            donor={donor}
          />
        );
      
      case 'nityaseva':
        return (
          <NityasevaTable
            nityasevas={nityasevas}
            title="Nitya Seva"
            totalnityaPages={totalnityaPages}
            nityapage={nityapage}
            setNityapage={setNityapage}
            paginationDisable={paginationDisable}
            donor={donor}
            onStatusUpdate={handleNityasevaStatusUpdate}
          />
        );
      
      case 'purchases':
        return (
          <PurchaseTable
            purchases={purchases}
            title="Purchases"
            totalsalePages={totalsalePages}
            salepage={salepage}
            salesetPage={setSalepage}
            paginationDisable={paginationDisable}
            donor={donor}
          />
        );
      
      case 'all':
      default:
        return (
          <>
            <DonationTable
              donations={donations}
              title="Donations"
              totalPages={donationTotalPages}
              page={donationPage}
              setPage={setDonationPage}
              paginationDisable={paginationDisable}
              donor={donor}
            />

            <NityasevaTable
              nityasevas={nityasevas}
              title="Nitya Seva"
              totalnityaPages={totalnityaPages}
              nityapage={nityapage}
              setNityapage={setNityapage}
              paginationDisable={paginationDisable}
              donor={donor}
              onStatusUpdate={handleNityasevaStatusUpdate}
            />

            <PurchaseTable
              purchases={purchases}
              title="Purchases"
              totalsalePages={totalsalePages}
              salepage={salepage}
              salesetPage={setSalepage}
              paginationDisable={paginationDisable}
              donor={donor}
            />
          </>
        );
    }
  };

  return (
    <>
      <GoToTop />
      <MetaData title="Dashboard - ISKCON of Bhiwandi" />

      <section className="dashboard-table">
        <div className="container">
          {/* Top Navigation */}
          <div className="top-head pb-3 pb-lg-0 heading">
            <h2 className="head-1">Transaction History</h2>
            <div className="d-flex justify-content-center flex-wrap gap-2 mb-4">
              <DownloadTenbe />
              <button
                className="custom-btn-cls box-hover text-uppercase"
                onClick={() => navigate("/Service-requests")}
              >
                Service Requests
              </button>
              <button
                className="custom-btn-cls box-hover text-uppercase"
                onClick={() => navigate("/join-family")}
              >
                Join Family
              </button>
              <button
                className="custom-btn-cls box-hover text-uppercase active"
                onClick={() => navigate("/all-transactions")}
              >
                All Transactions
              </button>
              <button
                className="custom-btn-cls box-hover"
                onClick={() => navigate("/donation-form")}
              >
                Donate More
              </button>
            </div>
          </div>

          {/* Transaction Tabs */}
          <div className="transaction-tabs mb-4">
            <div className="row justify-content-center">
              <div className="col-12">
                <ul className="nav nav-pills nav-justified" role="tablist">
                  {tabs.map((tab) => (
                    <li className="nav-item" key={tab.id} role="presentation">
                      <button
                        className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        style={{
                          backgroundColor: activeTab === tab.id ? '#dfa21b' : 'transparent',
                          color: activeTab === tab.id ? 'white' : '#333',
                          border: '1px solid #dfa21b',
                          margin: '0 2px',
                          borderRadius: '10px',
                          padding: '5px 10px',
                          fontWeight: '600',
                          transition: 'all 0.3s ease',
                          width: '393px'
                        }}
                      >
                        <span className="me-2">{tab.icon}</span>
                        {tab.label}
                        {/* Show count badges */}
                        {tab.id === 'donations' && donations.length > 0 && (
                          <span className="badge bg-light text-dark ms-2">{donations.length}</span>
                        )}
                        {tab.id === 'nityaseva' && nityasevas.length > 0 && (
                          <span className="badge bg-light text-dark ms-2">{nityasevas.length}</span>
                        )}
                        {tab.id === 'purchases' && purchases.length > 0 && (
                          <span className="badge bg-light text-dark ms-2">{purchases.length}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {paginationDisable && (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading transactions...</p>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            <div className={`tab-pane fade ${activeTab ? 'show active' : ''}`}>
              {renderTabContent()}
            </div>
          </div>

          {/* Empty state for no data */}
          {!paginationDisable && 
           donations.length === 0 && 
           nityasevas.length === 0 && 
           purchases.length === 0 && (
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="fas fa-receipt" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              </div>
              <h4 className="text-muted mb-3">No Transactions Found</h4>
              <p className="text-muted mb-4">
                You haven't made any transactions yet. Start by making a donation!
              </p>
              <button
                className="custom-btn-cls box-hover"
                onClick={() => navigate("/donation-form")}
              >
                Make Your First Donation
              </button>
            </div>
          )}
        </div>
      </section>

     
    </>
  );
};

export default DmsAllTransactions;